import { useState } from 'react';
import { toast } from 'sonner';
import { Check, Clock, KeyRound, Loader2, X } from 'lucide-react';

import {
  useUpdateAdminUserMutation,
  useAdminUserVerificationMutation,
  type VerificationField,
} from '@/client/api/admin/users.mutation';
import { useAdminUserLastSeenQuery, useAdminUserMfaQuery } from '@/client/api/admin/users.query';
import type {
  AdminUserAccount,
  AdminUserUpdatePayload,
} from '@/client/types/admin/users-orgs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { describeUserTimeZone, formatDateTime } from '@/client/utils/datetime';

type DetailUser = AdminUserAccount & { uid: number };

/** Fields `PUT /api/admin/users/<uid>` actually persists. Everything else —
 *  `active`, `verified*`, `admin_hold` — is stripped server-side. */
const EDITABLE_FIELDS: Array<{
  key: keyof Omit<AdminUserUpdatePayload, 'uid'>;
  label: string;
  placeholder?: string;
}> = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'other_names', label: 'Other names' },
  { key: 'preferred_name', label: 'Preferred name' },
  { key: 'email2', label: 'Secondary email' },
  { key: 'phone2', label: 'Secondary phone' },
  { key: 'address_res', label: 'Residential address' },
  { key: 'address_post', label: 'Postal address' },
  { key: 'dob', label: 'Date of birth', placeholder: 'YYYY-MM-DD' },
  { key: 'country_iso2', label: 'Country (ISO2)', placeholder: 'GH' },
  { key: 'locale', label: 'Locale', placeholder: 'en_GB' },
  { key: 'timezone', label: 'Timezone', placeholder: 'Africa/Accra' },
];

// Timezone-aware + labelled (see utils/datetime) so timestamps aren't ambiguous.
const formatDate = (value?: string | null) => formatDateTime(value);

/** Detect the backend's `{error:"unique"}` email-collision marker. */
const isDuplicateEmail = (error: any) =>
  error?.data?.data?.error === 'unique' ||
  error?.data?.error === 'unique' ||
  error?.response?.data?.error === 'unique';

/** KYC fields the admin can manually flip, mapped to their DB columns.
 *  `requires` names a value column that must be set for the row to be relevant
 *  (secondary email/phone only apply when the user actually has one). */
const VERIFY_FIELDS: {
  label: string;
  field: VerificationField;
  requires?: 'email2' | 'phone2';
}[] = [
  { label: 'Primary email', field: 'email1_ver' },
  { label: 'Secondary email', field: 'email2_ver', requires: 'email2' },
  { label: 'Primary phone', field: 'phone1_ver' },
  { label: 'Secondary phone', field: 'phone2_ver', requires: 'phone2' },
  { label: 'ID', field: 'verified_id' },
  { label: 'Photo', field: 'verified_photo' },
  { label: 'Address', field: 'verified_address' },
  { label: 'Age', field: 'age_ver' },
];

const OverviewTab = ({ user, authid }: { user: DetailUser; authid: string | null }) => {
  const updateMutation = useUpdateAdminUserMutation();
  const verifyMutation = useAdminUserVerificationMutation();
  const [busyField, setBusyField] = useState<VerificationField | null>(null);

  const toggleVerification = async (field: VerificationField, next: 0 | 1) => {
    setBusyField(field);
    const toastId = toast.loading(next ? 'Marking verified…' : 'Removing verification…');
    try {
      await verifyMutation.mutateAsync({ uid: user.uid, field, value: next });
      toast.success(next ? 'Marked verified' : 'Verification removed', {
        id: toastId,
        richColors: true,
      });
    } catch (error: any) {
      toast.error('Failed to update verification', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
        richColors: true,
      });
    } finally {
      setBusyField(null);
    }
  };
  const {
    data: lastSeen,
    isLoading: lastSeenLoading,
    isError: lastSeenError,
  } = useAdminUserLastSeenQuery(authid);
  const mfa = useAdminUserMfaQuery(user.uid);

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<Partial<AdminUserUpdatePayload>>({});

  const startEditing = () => {
    setForm(
      EDITABLE_FIELDS.reduce<Partial<AdminUserUpdatePayload>>((acc, field) => {
        const current = user[field.key as keyof typeof user];
        return { ...acc, [field.key]: (current as string | null) ?? '' };
      }, {})
    );
    setIsEditing(true);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const toastId = toast.loading('Saving profile…');
    try {
      await updateMutation.mutateAsync({ uid: user.uid, ...form } as AdminUserUpdatePayload);
      toast.success('Profile updated', { id: toastId, richColors: true, duration: 4000 });
      setIsEditing(false);
    } catch (error: any) {
      toast.error('Failed to update profile', {
        id: toastId,
        description: isDuplicateEmail(error)
          ? 'That email address is already in use by another account.'
          : (error?.message ?? 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const completion = user.comp_rate;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
            {isEditing ? (
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={startEditing}>
                Edit
              </Button>
            )}
          </div>

          {!isEditing ? (
            <dl className="space-y-2">
              {EDITABLE_FIELDS.map(field => (
                <div
                  key={field.key}
                  className="grid grid-cols-3 items-center gap-3 rounded-md bg-gray-50 p-2.5"
                >
                  <dt className="text-sm font-medium text-gray-500">{field.label}</dt>
                  <dd className="col-span-2 break-words text-sm text-gray-900">
                    {(() => {
                      const rawVal = user[field.key as keyof typeof user];
                      // Timezone is stored as a numeric UTC offset (e.g. -7) — a
                      // bare "-7" is meaningless, so render it as UTC-07:00 plus
                      // the user's current local time. (Handled first so offset 0
                      // = UTC isn't mistaken for "Not set".)
                      if (field.key === 'timezone') {
                        const tz = describeUserTimeZone(rawVal as string | number | null);
                        if (!tz) return <span className="text-gray-400">Not set</span>;
                        return (
                          <span className="flex flex-wrap items-baseline gap-x-2">
                            <span>{tz.label}</span>
                            {tz.detail && (
                              <span className="text-xs text-gray-500">{tz.detail}</span>
                            )}
                          </span>
                        );
                      }
                      const raw = rawVal as string | null;
                      if (!raw) return <span className="text-gray-400">Not set</span>;
                      return raw;
                    })()}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <form onSubmit={handleSaveProfile} className="space-y-3">
              {EDITABLE_FIELDS.map(field => (
                <div key={field.key} className="space-y-1">
                  <Label htmlFor={`field-${field.key}`}>{field.label}</Label>
                  <Input
                    id={`field-${field.key}`}
                    placeholder={field.placeholder}
                    value={(form[field.key] as string) ?? ''}
                    onChange={e =>
                      setForm(prev => ({ ...prev, [field.key]: e.target.value }))
                    }
                  />
                </div>
              ))}
              <p className="text-xs text-gray-500">
                Active, verification and suspension states are not editable here — the admin
                API strips them from profile updates. Suspension has its own control on the
                Danger zone tab.
              </p>
              <Button type="submit" disabled={updateMutation.isPending} className="w-full">
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save changes'
                )}
              </Button>
            </form>
          )}
        </section>

        <section className="space-y-2 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Identifiers</h2>
          {[
            ['uid', String(user.uid)],
            ['authid', user.authid],
            ['ffid', user.ffid ?? '—'],
            ['Primary email', user.email1 ?? '—'],
            ['Primary phone', user.phone1 ?? '—'],
            ['Created', formatDate(user.date_added)],
            ['Modified', formatDate(user.date_modified)],
          ].map(([label, value]) => (
            <div
              key={label}
              className="grid grid-cols-3 items-center gap-3 rounded-md bg-gray-50 p-2.5"
            >
              <span className="text-sm font-medium text-gray-500">{label}</span>
              <span className="col-span-2 break-all font-mono text-sm text-gray-900">
                {value}
              </span>
            </div>
          ))}
        </section>
      </div>

      <div className="space-y-6">
        {/* Verification / KYC — admin can flip each flag manually. */}
        <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Verification</h2>
          <div className="space-y-2">
            {VERIFY_FIELDS.filter(
              ({ requires }) => !requires || Boolean(user[requires as keyof typeof user])
            ).map(({ label, field }) => {
              const ok = (user[field as keyof typeof user] as number) === 1;
              const busy = busyField === field;
              return (
                <div
                  key={field}
                  className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                    ok ? 'border-emerald-100 bg-emerald-50' : 'border-gray-100 bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-1.5 text-sm">
                    <span className="font-medium text-gray-700">{label}</span>
                    <span
                      className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                        ok ? 'text-emerald-700' : 'text-gray-400'
                      }`}
                    >
                      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                      {ok ? 'Verified' : 'Unverified'}
                    </span>
                  </span>
                  <Button
                    variant={ok ? 'ghost' : 'outline'}
                    size="sm"
                    disabled={busy}
                    onClick={() => toggleVerification(field, ok ? 0 : 1)}
                    className={ok ? 'text-gray-500' : 'text-emerald-700'}
                  >
                    {busy ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : ok ? (
                      'Unverify'
                    ) : (
                      'Verify'
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
          {/* Read-only: turning it off for a user lives in Danger Zone. */}
          <div
            className={`flex items-center justify-between gap-2 rounded-md border px-3 py-2 ${
              mfa.data?.enabled ? 'border-emerald-100 bg-emerald-50' : 'border-gray-100 bg-gray-50'
            }`}
          >
            <span className="flex items-center gap-1.5 text-sm">
              <KeyRound className="h-3.5 w-3.5 text-gray-500" />
              <span className="font-medium text-gray-700">Two-step verification</span>
            </span>
            <span
              className={`text-xs font-semibold ${mfa.data?.enabled ? 'text-emerald-700' : 'text-gray-400'}`}
            >
              {mfa.isLoading
                ? '…'
                : mfa.isError
                  ? 'Unknown'
                  : mfa.data?.enabled
                    ? `On · ${mfa.data.method === 'sms' ? 'text message' : 'email'}`
                    : 'Off'}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            Manual overrides. Persisting these needs backend support for admin verification
            (see notes) — the control is wired and ready.
          </p>
        </section>

        {completion != null && (
          <section className="space-y-2 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">Profile completion</h2>
            <div className="flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                <div
                  className={`h-full rounded-full ${
                    completion >= 80
                      ? 'bg-emerald-500'
                      : completion >= 40
                      ? 'bg-amber-500'
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(100, Math.max(0, completion))}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-gray-700">{completion}%</span>
            </div>
          </section>
        )}

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-900">Last seen</h2>
          </div>
          {lastSeenLoading ? (
            <Skeleton className="h-4 w-40" />
          ) : lastSeenError ? (
            <p className="text-sm text-red-600">Could not load last seen.</p>
          ) : lastSeen?.last_seen_at ? (
            <p className="text-sm text-gray-900">{formatDate(lastSeen.last_seen_at)}</p>
          ) : (
            <p className="text-sm italic text-gray-400">No last-seen record.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default OverviewTab;
